import type { QueryClient, QueryKey } from "@tanstack/react-query";
import type { SynthesesGroupedContractsView } from "@/pages/dashboard/syntheses/useSynthesesGroupedContractsRead";

export const DASHBOARD_QUERY_GC_TIME = 1000 * 60 * 60 * 12;

export const DASHBOARD_QUERY_STALE_TIMES = {
  indicators: 1000 * 60 * 15,
  indicatorReporting: 1000 * 60 * 5,
  programsInfo: 1000 * 60 * 15,
  reporting: 1000 * 60 * 5,
  pendingVerdeoFundings: 1000 * 60 * 5,
  subcontractors: 1000 * 60 * 15,
  subcontractorAccess: 1000 * 60 * 15,
  groupedContracts: 1000 * 60 * 10,
  listingProjects: 1000 * 60 * 15,
  funders: 1000 * 60 * 15,
  funderRepresentants: 1000 * 60 * 15,
  callForProjects: 1000 * 60 * 15,
  coworkers: 1000 * 60 * 15,
} as const;

export const dashboardQueryKeys = {
  indicators: "dashboardIndicators",
  indicatorReporting: (indicatorId: number | string) =>
    ["dashboardIndicatorReporting", String(indicatorId)] as const,
  programsInfo: "programsInfo",
  reporting: "dashboardReporting",
  pendingVerdeoFundings: "dashboardPendingVerdeoFundings",
  subcontractors: "dashboardSubcontractors",
  subcontractorAccess: (memberIdsKey: string) =>
    ["dashboardSubcontractorAccess", memberIdsKey] as const,
  groupedContracts: (view: SynthesesGroupedContractsView) =>
    ["dashboardGroupedContracts", view] as const,
  listingProjects: "AapProject",
  funders: "dashboardFunders",
  funderRepresentants: (funderId: number | string) =>
    ["dashboardFunderRepresentants", String(funderId)] as const,
  callForProjects: "dashboardCallForProjects",
  coworkers: "dashboardCoworkers",
};

const DASHBOARD_PERSISTED_QUERY_KEY_ROOTS = new Set([
  dashboardQueryKeys.indicators,
  "dashboardIndicatorReporting",
  dashboardQueryKeys.programsInfo,
  dashboardQueryKeys.reporting,
  dashboardQueryKeys.pendingVerdeoFundings,
  dashboardQueryKeys.subcontractors,
  "dashboardSubcontractorAccess",
  "dashboardGroupedContracts",
  dashboardQueryKeys.listingProjects,
  dashboardQueryKeys.funders,
  "dashboardFunderRepresentants",
  dashboardQueryKeys.callForProjects,
  dashboardQueryKeys.coworkers,
]);

export function isDashboardPersistedQueryKey(queryKey: QueryKey) {
  if (!Array.isArray(queryKey) || typeof queryKey[0] !== "string") {
    return false;
  }

  return DASHBOARD_PERSISTED_QUERY_KEY_ROOTS.has(queryKey[0]);
}

const DASHBOARD_DERIVED_QUERY_KEY_ROOTS = new Set([
  "dashboardIndicatorReporting",
]);

const dashboardInvalidationMatrix = {
  projectFunding: [
    dashboardQueryKeys.reporting,
    "dashboardIndicatorReporting",
    dashboardQueryKeys.pendingVerdeoFundings,
    "dashboardGroupedContracts",
    dashboardQueryKeys.listingProjects,
  ],
  projectStatus: [
    dashboardQueryKeys.programsInfo,
    dashboardQueryKeys.listingProjects,
  ],
  projectLifecycle: [
    dashboardQueryKeys.programsInfo,
    dashboardQueryKeys.listingProjects,
  ],
  projectPerformance: [
    dashboardQueryKeys.reporting,
    "dashboardIndicatorReporting",
  ],
  projectIndicatorDefinition: [
    dashboardQueryKeys.indicators,
    dashboardQueryKeys.reporting,
    "dashboardIndicatorReporting",
  ],
  dashboardIndicators: [
    dashboardQueryKeys.indicators,
    dashboardQueryKeys.reporting,
    "dashboardIndicatorReporting",
  ],
  subcontractors: [
    dashboardQueryKeys.subcontractors,
    "dashboardSubcontractorAccess",
    "dashboardGroupedContracts",
  ],
  funders: [
    dashboardQueryKeys.funders,
    "dashboardFunderRepresentants",
    "dashboardGroupedContracts",
  ],
  callForProjects: [
    dashboardQueryKeys.callForProjects,
  ],
  coworkers: [
    dashboardQueryKeys.coworkers,
  ],
} as const;

export type DashboardInvalidationReason = keyof typeof dashboardInvalidationMatrix;

export function getDashboardInvalidationQueryKeys(
  reasons: DashboardInvalidationReason | DashboardInvalidationReason[],
): QueryKey[] {
  const roots = new Set<string>();
  const normalizedReasons = Array.isArray(reasons) ? reasons : [reasons];

  normalizedReasons.forEach((reason) => {
    dashboardInvalidationMatrix[reason].forEach((root) => {
      roots.add(root);
    });
  });

  return Array.from(roots).map((root) => [root]);
}

export function invalidateDashboardQueries(
  queryClient: QueryClient,
  reasons: DashboardInvalidationReason | DashboardInvalidationReason[],
) {
  getDashboardInvalidationQueryKeys(reasons).forEach((queryKey) => {
    const root = String(queryKey[0] || "");

    if (DASHBOARD_DERIVED_QUERY_KEY_ROOTS.has(root)) {
      queryClient.removeQueries({
        exact: false,
        queryKey,
      });
      return;
    }

    void queryClient.invalidateQueries({
      exact: false,
      queryKey,
    });
  });
}

export function updateDashboardProgramsInfoProject(
  queryClient: QueryClient,
  nextProject: Record<string, any> | null | undefined,
) {
  const projectId = Number(nextProject?.id || 0);
  if (!projectId) {
    return;
  }

  const upsertProjectInPrograms = (current: unknown) => {
    if (!Array.isArray(current)) {
      return current;
    }

    let hasChanged = false;
    const nextPrograms = current.map((program: any) => {
      const projects = Array.isArray(program?.projects) ? program.projects : [];
      let programHasChanged = false;
      const nextProjects = projects.map((programProject: any) => {
        if (Number(programProject?.id || 0) !== projectId) {
          return programProject;
        }

        hasChanged = true;
        programHasChanged = true;
        return {
          ...programProject,
          ...nextProject,
        };
      });

      return !programHasChanged
        ? program
        : {
            ...program,
            projects: nextProjects,
          };
    });

    return hasChanged ? nextPrograms : current;
  };

  queryClient.setQueriesData(
    {
      exact: false,
      queryKey: [dashboardQueryKeys.programsInfo],
    },
    upsertProjectInPrograms,
  );
}
